import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, EscalationBadge, RagBadge } from "@/components/widgets";
import { ComplaintDrawer } from "@/components/ComplaintDrawer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Flame, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const LEVEL_DESC = {
  5: "Managing Director — daily consolidated digest",
  4: "VP Sales / VP Service",
  3: "Cluster Heads",
  2: "Outlet Sales / Service Managers",
  1: "Outlet CCM / CRM Executives",
};

const matchesSearch = (e, search) => {
  if (!search) return true;
  const q = search.toLowerCase();
  return (
    e.complaint_number?.toLowerCase().includes(q) ||
    e.outlet_name?.toLowerCase().includes(q) ||
    e.category?.toLowerCase().includes(q) ||
    e.owner?.toLowerCase().includes(q)
  );
};

const LevelHeader = ({ lv, navigate }) => (
  <div className={cn("flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap",
    lv.level === 5 ? "bg-[hsl(var(--cx-crimson)/0.05)]" : "bg-[hsl(var(--cx-surface))]")}>
    <div className="flex items-center gap-2.5 flex-wrap">
      {lv.level === 5 && <Flame size={16} className="text-[hsl(var(--cx-crimson))]" />}
      <EscalationBadge level={lv.level} />
      <span className="font-serif text-base">{lv.label} level</span>
      <span className="text-xs text-muted-foreground hidden sm:inline">alerts: {LEVEL_DESC[lv.level]}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="kpi-value text-lg tabular" data-testid={`escalation-level-count-${lv.level}`}>
        {lv.count.toLocaleString()}
      </span>
      {lv.count > 0 && (
        <button
          className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate(`/complaints?escalation_level=${lv.level}`)}
          data-testid={`escalation-level-viewall-${lv.level}`}
        >
          View all in Complaints
        </button>
      )}
    </div>
  </div>
);

const LevelRowsTable = ({ lv, rows, onOpen }) => (
  <div className="overflow-x-auto">
    <Table className="cx-table min-w-[820px]" data-testid={`escalation-level-table-${lv.level}`}>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Complaint</TableHead>
          <TableHead>Outlet</TableHead>
          <TableHead>Function</TableHead>
          <TableHead>Category</TableHead>
          <TableHead className="text-right">Days Open</TableHead>
          <TableHead>Owner / SA</TableHead>
          <TableHead className="pr-4">Priority</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.id} className="cursor-pointer" data-testid={`escalation-row-${e.complaint_number}`}
            onClick={() => onOpen(e.id)}>
            <TableCell className="pl-4 font-medium whitespace-nowrap">{e.complaint_number}</TableCell>
            <TableCell className="whitespace-nowrap">{e.outlet_code} <span className="text-xs text-muted-foreground">{e.outlet_name}</span></TableCell>
            <TableCell><Badge variant="outline" className="text-[0.65rem]">{e.function}</Badge></TableCell>
            <TableCell className="max-w-[170px] truncate">{e.category}</TableCell>
            <TableCell className="text-right"><RagBadge rag={e.rag} days={e.days_open} /></TableCell>
            <TableCell className="max-w-[150px] truncate">{e.owner || "—"}</TableCell>
            <TableCell className="pr-4 text-xs">{e.priority}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {lv.count > lv.items.length && (
      <p className="px-4 py-2 text-xs text-muted-foreground border-t">
        Showing oldest {lv.items.length} of {lv.count.toLocaleString()} — use “View all in Complaints” for the full list.
      </p>
    )}
  </div>
);

/* Empty / filtered-out / data states without nested ternaries. */
const LevelBody = ({ lv, rows, onOpen }) => {
  if (lv.count === 0) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">None at this level.</p>;
  }
  if (rows.length === 0) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">No shown rows match your filter.</p>;
  }
  return <LevelRowsTable lv={lv} rows={rows} onOpen={onOpen} />;
};

const LevelCard = ({ lv, search, navigate, onOpen }) => {
  const rows = lv.items.filter((e) => matchesSearch(e, search));
  return (
    <Card className={cn("overflow-hidden", lv.level === 5 && lv.count > 0 && "border-[hsl(var(--cx-crimson)/0.45)]")}
      data-testid={`escalation-level-card-${lv.level}`}>
      <LevelHeader lv={lv} navigate={navigate} />
      <LevelBody lv={lv} rows={rows} onOpen={onOpen} />
    </Card>
  );
};

/* Loading / empty / data states without nested ternaries. */
const EscalationsBody = ({ loading, data, search, navigate, onOpen }) => {
  if (loading || !data) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`esc-skeleton-${i}`} className="h-24" />)}
      </div>
    );
  }
  if (data.total === 0) {
    return (
      <Card>
        <EmptyState title="No open escalations" subtitle="Every open complaint is within its resolution window." />
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {data.levels.map((lv) => (
        <LevelCard key={lv.level} lv={lv} search={search} navigate={navigate} onOpen={onOpen} />
      ))}
    </div>
  );
};

/**
 * READ-ONLY escalation matrix. Levels are computed automatically from complaint
 * aging (thresholds configurable by AGM). Escalations clear only when the
 * complaint is closed in Salesforce and the next dump is uploaded.
 */
export default function EscalationsPage() {
  const { user, fnFilter } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const effectiveFn = user?.function_scope !== "Both" ? user.function_scope : fnFilter;

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/escalations", { params: { function: effectiveFn, per_level: 25 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [effectiveFn]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.thresholds;
  const openComplaint = (id) => { setDrawerId(id); setDrawerOpen(true); };

  return (
    <div data-testid="escalations-page">
      <PageHeader
        title="Escalation Matrix"
        subtitle={
          t
            ? `Automatic, time-based: L1 from day 0 · L2 ≥${t.l2}d · L3 ≥${t.l3}d · L4 ≥${t.l4}d · L5 ≥${t.l5}d · clears on closure in Salesforce`
            : "Automatic, time-based escalation levels"
        }
      >
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Filter shown rows…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 w-[200px]" data-testid="escalations-search-input" />
        </div>
      </PageHeader>

      <EscalationsBody loading={loading} data={data} search={search} navigate={navigate} onOpen={openComplaint} />

      <ComplaintDrawer complaintId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
